import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { mediasRef } from '../../../api/firebase/firestore/media';
import { usersRef } from '../../../api/firebase/firestore/user';
import { getPosts } from '../../../api/microcms/article';
import { primaryButtonBackground } from '../../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { screenType } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import ArticleCard from '../../uiParts/ArticleCard';
import CustomLoading from '../../uiParts/CustomLoading';
import NothingGivenList from '../../uiParts/NothingGivenList';
import Sidebar from '../../uiParts/Sidebar';

import type { ArticleType } from '../../../api/microcms/article/articleType';

const styles = {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  primaryButtonBackground
};

const PublisherMypageArticle: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [articleList, setArticleList] = React.useState<ArticleType[]>([]);

  useEffect(() => {
    document.title = t('title.publisher.articleList');
  });

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (currentUser) {
        // ユーザーからcompanyIdを取得
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        const companyId = userSnap.data()?.companyId as string;
        // companyIdから記事一覧を取得
        const data = await getPosts(companyId);

        const promises = data.map(async (article) => {
          const mediaId = article.mediaId;
          if (mediaId) {
            const mediaRef = doc(mediasRef, mediaId);
            const mediaSnap = await getDoc(mediaRef);
            const draftFlg = mediaSnap.data()?.draftFlg as boolean;
            if (draftFlg === undefined || draftFlg === null) {
              return undefined;
            }
            return article;
          }
          return undefined;
        });

        const result = await Promise.all(promises);
        const articles = result.filter(
          (item): item is ArticleType => item !== undefined
        );
        setArticleList(articles);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.publisher} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: { sm: '24px', xs: '16px' },
            position: 'static',
            rowGap: { sm: '24px', xs: '16px' },
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.articleList.title')}
            </Typography>
            <Button
              variant="contained"
              sx={[
                styles.primaryButtonBackground,
                {
                  '@media screen and (max-width:960px)': {
                    height: '30px'
                  }
                }
              ]}
              onClick={() => navigate('/publisher/mypage/article/edit')}
            >
              ＋ {t('mypage.articleList.add')}
            </Button>
          </Typography>
          {articleList.length > 0 ? (
            articleList.map((article, articleIndex) => (
              <ArticleCard key={articleIndex} article={article} />
            ))
          ) : (
            <NothingGivenList
              title={t('mypage.articleList.empty')}
              detail={t('mypage.articleList.emptyDesc')}
            />
          )}
        </Typography>
      </Typography>
    </>
  );
};

export default PublisherMypageArticle;
